import { CardDef, CardInstance, DeckDef, PlayerSide, PRNGState } from '../types.js';
import { getConfig } from '../config.js';
import { shuffleWith } from './PRNG.js';

/** Mint a fresh instance id, bumping the per-state counter. Lives on state
 *  (rather than module-global) so concurrent AI simulations don't collide. */
export function createInstanceId(counter: { nextInstanceId: number }): string {
  return `ci_${++counter.nextInstanceId}`;
}

/** Seeded shuffle using a PRNG state. Mutates `rng` (advances the stream);
 *  returns a fresh array. */
export function shuffle<T>(rng: PRNGState, array: T[]): T[] {
  return shuffleWith(rng, array);
}

export function buildDeck(
  deckDef: DeckDef,
  cardCatalog: Map<string, CardDef>,
  owner: PlayerSide,
  counter: { nextInstanceId: number },
): { instances: CardInstance[]; errors: string[] } {
  const instances: CardInstance[] = [];
  const errors: string[] = [];
  const config = getConfig();

  let totalCards = 0;
  for (const entry of deckDef.cards) {
    const def = cardCatalog.get(entry.id);
    if (!def) {
      errors.push(`Unknown card: ${entry.id}`);
      continue;
    }
    if (entry.count > config.maxCopiesPerCard) {
      errors.push(`Too many copies of ${entry.id}: ${entry.count} (max ${config.maxCopiesPerCard})`);
    }
    for (let i = 0; i < entry.count; i++) {
      instances.push({
        instanceId: createInstanceId(counter),
        cardId: def.id,
        owner,
        currentPower: def.type === 'character' ? def.power : 0,
        armor: 0,
        isFresh: false,
        isExhausted: false,
        supports: [],
        zone: 'deck',
        skipNextRefresh: false,
        abilityDisabled: false,
      });
      totalCards++;
    }
  }

  if (totalCards < config.minDeckSize) {
    errors.push(`Deck has ${totalCards} cards (minimum ${config.minDeckSize})`);
  }

  return { instances, errors };
}

export function drawCards(
  deck: string[],
  count: number
): { drawn: string[]; remaining: string[] } {
  const drawn = deck.slice(0, count);
  const remaining = deck.slice(count);
  return { drawn, remaining };
}
